import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
	IconButton,
	Chip,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Divider
} from "@material-ui/core";
import {
	MoreVert,
	Replay,
	Android,
	Check,
	PowerSettingsNew
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import PixelPurchaseModal from "../PixelPurchaseModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		flex: "none",
		alignSelf: "center",
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
}));

const resolveAiModeLabel = promptMode => {
	if (promptMode === "atendimento") {
		return i18n.t("messagesList.header.aiModeSdr");
	}
	if (promptMode === "postsale") {
		return i18n.t("messagesList.header.aiModePostSale");
	}
	return i18n.t("messagesList.header.aiActive");
};

const TicketActionButtons = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [aiMenuAnchor, setAiMenuAnchor] = useState(null);
	const [loading, setLoading] = useState(false);
	const [pixelModalOpen, setPixelModalOpen] = useState(false);
	const [pixelTracking, setPixelTracking] = useState(null);
	const [aiEnabled, setAiEnabled] = useState(false);
	const [aiPromptMode, setAiPromptMode] = useState(null);
	const [aiToggling, setAiToggling] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const aiMenuOpen = Boolean(aiMenuAnchor);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		if (!ticket?.id) return undefined;

		const fetchPixelTracking = async () => {
			try {
				const { data } = await api.get(`/pixel/ticket/${ticket.id}`);
				setPixelTracking(data.pixelTracking);
			} catch (err) {
				setPixelTracking(null);
			}
		};

		fetchPixelTracking();
	}, [ticket?.id]);

	useEffect(() => {
		const socket = openSocket();
		if (!socket || !ticket?.id) return undefined;

		const handlePixelTracking = (data) => {
			if (
				data.action === "update" &&
				String(data.pixelTracking?.ticketId) === String(ticket.id)
			) {
				setPixelTracking(data.pixelTracking);
			}
		};

		socket.on("pixelTracking", handlePixelTracking);
		return () => {
			socket.off("pixelTracking", handlePixelTracking);
		};
	}, [ticket?.id]);

	useEffect(() => {
		setAiEnabled(Boolean(ticket?.aiEnabled));
		setAiPromptMode(ticket?.aiPromptMode || null);
	}, [ticket?.aiEnabled, ticket?.aiPromptMode, ticket?.id]);

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleOpenAiMenu = e => {
		setAiMenuAnchor(e.currentTarget);
	};

	const handleCloseAiMenu = () => {
		setAiMenuAnchor(null);
	};

	const handleSetAiMode = async promptMode => {
		if (!ticket?.id) return;
		handleCloseAiMenu();
		setAiToggling(true);
		try {
			const { data } = await api.post(`/ai/ticket/${ticket.id}/toggle`, {
				enabled: true,
				promptMode
			});
			if (data?.ticket) {
				setAiEnabled(Boolean(data.ticket.aiEnabled));
				setAiPromptMode(data.ticket.aiPromptMode || null);
			}
		} catch (err) {
			toastError(err);
		} finally {
			setAiToggling(false);
		}
	};

	const handleDisableAi = async () => {
		if (!ticket?.id) return;
		handleCloseAiMenu();
		setAiToggling(true);
		try {
			const { data } = await api.post(`/ai/ticket/${ticket.id}/toggle`, {
				enabled: false
			});
			if (data?.ticket) {
				setAiEnabled(Boolean(data.ticket.aiEnabled));
				setAiPromptMode(data.ticket.aiPromptMode || null);
			}
		} catch (err) {
			toastError(err);
		} finally {
			setAiToggling(false);
		}
	};

	const handleUpdateTicketStatus = async (e, status, userId) => {
		setLoading(true);
		try {
			await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			if (status === "open") {
				history.push(`/tickets/${ticket.id}`);
			} else {
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	return (
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={aiToggling}
						startIcon={<Android />}
						size="small"
						variant={aiEnabled ? "contained" : "outlined"}
						color={aiEnabled ? "secondary" : "default"}
						onClick={handleOpenAiMenu}
					>
						{aiEnabled
							? resolveAiModeLabel(aiPromptMode)
							: i18n.t("messagesList.header.buttons.ai")}
					</ButtonWithSpinner>
					<Menu
						anchorEl={aiMenuAnchor}
						open={aiMenuOpen}
						onClose={handleCloseAiMenu}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						<MenuItem onClick={() => handleSetAiMode("atendimento")}>
							<ListItemIcon>
								{aiEnabled && aiPromptMode === "atendimento" ? (
									<Check fontSize="small" />
								) : null}
							</ListItemIcon>
							<ListItemText primary={i18n.t("messagesList.header.aiModeSdr")} />
						</MenuItem>
						<MenuItem onClick={() => handleSetAiMode("postsale")}>
							<ListItemIcon>
								{aiEnabled && aiPromptMode === "postsale" ? (
									<Check fontSize="small" />
								) : null}
							</ListItemIcon>
							<ListItemText
								primary={i18n.t("messagesList.header.aiModePostSale")}
							/>
						</MenuItem>
						{aiEnabled && (
							<>
								<Divider />
								<MenuItem onClick={handleDisableAi}>
									<ListItemIcon>
										<PowerSettingsNew fontSize="small" />
									</ListItemIcon>
									<ListItemText
										primary={i18n.t("messagesList.header.aiDisable")}
									/>
								</MenuItem>
							</>
						)}
					</Menu>
					{aiEnabled && (
						<Chip
							size="small"
							label={resolveAiModeLabel(aiPromptMode)}
							color="secondary"
						/>
					)}
					<ButtonWithSpinner
						size="small"
						variant="outlined"
						color={pixelTracking?.converted ? "default" : "primary"}
						onClick={() => setPixelModalOpen(true)}
					>
						{i18n.t("messagesList.header.buttons.pixel")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner>
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
					/>
					<PixelPurchaseModal
						open={pixelModalOpen}
						onClose={() => setPixelModalOpen(false)}
						ticket={ticket}
						pixelTracking={pixelTracking}
						onSuccess={setPixelTracking}
					/>
				</>
			)}
			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
		</div>
	);
};

export default TicketActionButtons;
